import { useState, useEffect, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useUploadListContext } from "src/contexts/UploadListContext";
import Loading from "src/components/Loading";
import { HttpMethod, callApi } from "src/services/apiService";
import { IInternalDNC, CpaSection } from "src/interfaces/Main/IInternalDNC";
import SaveInProgress from "src/components/SaveInProgress";
import ProgressBar from "src/components/ProgressBar";
import FileUploadContainer from "src/components/FileUploadContainer";
import DownloadDNCDNMDatabases from "./DownloadDNCDNMDatabases";
import UndoDNC from "./UndoDNC";
import SearchDNCDatabase from "./SearchDNCDatabase";
import axios, { AxiosProgressEvent } from "axios";
import { v4 as uuidv4 } from "uuid";

const InternalDNCMain = () => {
  const { projectId, campaignId } = useUploadListContext();
  const importTextAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const undoDNCRef = useRef<{ updateUndoDetails: () => void }>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [saveInProgress] = useState(false);
  const [fileUploadMessage, setFileUploadMessage] = useState("");
  const [isMessageError, setIsMessageError] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [formValues, setFormValues] = useState<IInternalDNC>();
  const [access, setAccess] = useState<CpaSection>();
  const [fetchedRegions, setFetchedRegions] = useState<
    { state: string; stateName: string }[]
  >([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const fetchComponentLoadItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiUrl = `main/dncdnm?projId=${projectId}&campaignId=${campaignId}`;
      const response = await callApi(apiUrl, HttpMethod.GET);
      if (response) {
        if (!response.cpaSection.Add) {
          window.location.href = "/main/upload-list";
        }
        setFormValues(response);
        setFetchedRegions(response.regions);
        setAccess(response.cpaSection);
      }
    } catch (error) {
      console.error("Failed to fetch sub menu:", error);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, campaignId]);

  useEffect(() => {
    if (projectId && campaignId) {
      fetchComponentLoadItems();
      updateUndoDetails();
    }
  }, [projectId, campaignId, fetchComponentLoadItems]);

  const updateUndoDetails = () => {
    if (undoDNCRef.current) {
      undoDNCRef.current.updateUndoDetails();
    }
  };

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmText.toLowerCase() === "delete") {
      setShowDeleteModal(false);
      setDeleteConfirmText("");
      // Proceed with the actual file upload in delete mode
      processFileUpload(pendingFiles, true);
    } else {
      alert('Please type "delete" to confirm.');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteConfirmText("");
    setPendingFiles([]);
    // Reset any upload progress
    setUploadProgress(0);
  };

  const processFileUpload = useCallback(
    async (files: File[], isDeleteMode = false) => {
      if (!files || files.length === 0) {
        console.error("No files selected.");
        return;
      }

      // File extension validation is now handled in onDrop
      setFileUploadMessage("");
      setIsMessageError(false);
      setUploadProgress(0);

      try {
        // Process each file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          // Generate GUID filename - match original backend pattern (guid.extension only)
          // Backend uses Guid.NewGuid().ToString("N") which is 32 hex chars without hyphens
          const fileExtension =
            file.name.lastIndexOf(".") > -1
              ? file.name.slice(file.name.lastIndexOf("."))
              : "";
          const guid = uuidv4().replace(/-/g, ""); // Remove hyphens to match backend format
          const fileNameWithGuid = `${guid}${fileExtension}`;

          // Step 1: Get SAS URL for upload
          const initiateResponse = await callApi(
            "main/dncdnm/initiate-upload",
            HttpMethod.POST,
            {
              fileName: fileNameWithGuid,
              fileSize: file.size,
              contentType: file.type,
            },
          );

          if (!initiateResponse?.uploadUrl) {
            throw new Error("Failed to get upload URL to Azure Storage");
          }

          // Step 2: Upload file directly to blob storage using SAS URL
          await axios({
            method: "put",
            url: initiateResponse.uploadUrl,
            data: file,
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": file.type,
            },
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const total = progressEvent.total || progressEvent.loaded;
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / total,
              );
              // Calculate progress across all files
              const fileProgress = percentCompleted / files.length;
              const previousFilesProgress = (i / files.length) * 100;
              setUploadProgress(previousFilesProgress + fileProgress);
            },
          });

          // Step 3: Submit the blob filename to queue processing
          const submitResponse = await callApi(
            `main/dncdnm/submit?projId=${projectId}&campaignId=${campaignId}&delete=${isDeleteMode}`,
            HttpMethod.POST,
            {
              fileName: file.name,
              blobFileName: fileNameWithGuid,
            },
          );

          if (submitResponse?.type !== "SUCCESS") {
            throw new Error(
              submitResponse?.message || "Failed to queue file for processing",
            );
          }
        }

        const fileNames = files.map((f) => f.name).join(", ");
        const action = isDeleteMode ? "deleted" : "uploaded";
        setFileUploadMessage(
          `File${files.length > 1 ? "s" : ""} "${fileNames}" sent for ${action} successfully.`,
        );
        setIsMessageError(false);
        updateUndoDetails();
      } catch (error: any) {
        console.error("Upload failed:", error);
        setFileUploadMessage(
          error.response?.data?.message ||
            error.message ||
            "Upload failed. Please try again.",
        );
        setIsMessageError(true);
      } finally {
        setUploadProgress(0);
      }
    },
    [projectId, campaignId],
  );

  const handleImportClick = async (addToMaster = false) => {
    if (importTextAreaRef.current && importTextAreaRef.current.value) {
      const input = importTextAreaRef.current.value.trim();
      setIsLoading(true);
      const phoneNumbers = input.split(/[\n,;|]+/).filter(Boolean);

      // Ensure each line has 10 digits. We do not care how they are formatted. Eg this is acceptable
      // (503) 936-7187
      // 503.936.7187
      // 503-936-7187
      const allValid = phoneNumbers.every(
        (number) => (number.match(/\d/g) || []).length === 10,
      );

      if (!allValid) {
        alert(
          "Please enter valid 10-digit phone numbers, separated by new lines, commas, semicolons, or pipes.",
        );
        setIsLoading(false);
        return;
      }
      const numbersToPost = phoneNumbers.map((number) =>
        Number(number.replace(/\D/g, "")),
      );

      const apiUrl = `main/dncdnm/import?projId=${projectId}&campaignId=${campaignId}&addToMaster=${addToMaster}`;
      const response = await callApi(apiUrl, HttpMethod.POST, numbersToPost);
      if (response.type.toLowerCase() === "success") {
        updateUndoDetails();
        if (importTextAreaRef.current) {
          importTextAreaRef.current.value = "";
        }
      } else {
        console.log("API Response:", response);
      }
      setIsLoading(false);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const files = acceptedFiles;

      if (!files || files.length === 0) {
        console.error("No files selected.");
        return;
      }

      // Validate file extensions first, before showing modal
      setFileUploadMessage("");
      setIsMessageError(false);

      const invalidFiles = files.filter(
        (file) => !/\.(txt|csv|zip)$/i.test(file.name),
      );

      if (invalidFiles.length > 0) {
        setFileUploadMessage(
          "File extension invalid. Please upload valid files. Only .txt, .csv, and .zip files are accepted.",
        );
        setIsMessageError(true);
        return;
      }

      // If delete mode is enabled, show confirmation modal
      if (deleteMode) {
        setPendingFiles(files);
        setShowDeleteModal(true);
        return;
      }

      // Otherwise, proceed with normal upload
      processFileUpload(files, false);
    },
    [deleteMode, processFileUpload],
  );

  const onDropRejected = useCallback((fileRejections: any[]) => {
    if (fileRejections.length > 0) {
      setFileUploadMessage(
        "Please upload valid files. Only .txt, .csv, and .zip files are accepted.",
      );
      setIsMessageError(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: true,
    // Removed accept property to allow custom validation in onDrop
  });

  if (isLoading || access === undefined) {
    return <Loading />;
  }

  return (
    <>
      <div className="debug-access d-none">
        <h5>Access</h5>
        <pre>{JSON.stringify(access, null, 2)}</pre>
      </div>
      <div className="d-flex justify-content-between">
        <div className="card col-6">
          <div className="card-header">
            Import Phone Numbers into Project's Do Not Call database{" "}
            <small>
              <i>(applies to all its campaigns)</i>
            </small>
          </div>
          <div className="card-body">
            {access?.Add && (
              <>
                <textarea
                  rows={4}
                  className="form-control"
                  ref={importTextAreaRef}
                  title="Enter 10-digit phone numbers"
                ></textarea>
                <button
                  onClick={() => handleImportClick(false)}
                  className="btn btn-primary mt-2 me-2"
                >
                  <i className="bi bi-arrow-up-circle"></i> Import
                </button>
                {!formValues?.isMaster && (
                  <button
                    onClick={() => handleImportClick(true)}
                    className="btn btn-primary mt-2"
                  >
                    Import to Project & Master
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {access.Undo && (
          <div>
            <UndoDNC ref={undoDNCRef} />
          </div>
        )}
      </div>

      <SearchDNCDatabase
        access={access}
        fetchedRegions={fetchedRegions}
        onActionComplete={updateUndoDetails}
      />

      {access.Upload && (
        <div className="card mt-5">
          <div className="card-header">
            Upload to Selected Project's Databases
          </div>
          <div className="card-body">
            <p>
              We accept a text file, a CSV file, or a zipped text or CSV file.
              Files must end in .txt, .csv, or .zip. If you upload a Zipfile,
              the Zipfile must contain only one file. Zipfiles at this time may
              not be password protected.
            </p>
            {uploadProgress > 0 && <ProgressBar progress={uploadProgress} />}
            <div className="d-flex gap-3 align-items-center">
              <label className="pe-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked
                  disabled
                />
                <span className="ps-2">Do Not Call</span>
              </label>
            </div>

            <div className="d-flex gap-2 mt-4 flex-wrap">
              <FileUploadContainer
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                isDragActive={isDragActive}
                title="UPLOAD FILES"
                iconClassName="bi-box-arrow-up"
              />
            </div>

            {/* Advanced Settings Toggle */}
            <div className="mt-3">
              <button
                onClick={toggleAdvancedSettings}
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--link-color)",
                  textDecoration: "none",
                  cursor: "pointer",
                  padding: "0.4rem",
                  borderRadius: "3px",
                  fontSize: "0.9rem",
                  transition: "background-color 0.2s, color 0.2s",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "var(--navbar-and-treeview-hover-bg)";
                  e.currentTarget.style.color = "var(--neutral-light)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.color = "var(--link-color)";
                }}
              >
                <i className="bi bi-gear me-2"></i>
                {showAdvancedSettings
                  ? "Hide Advanced Settings"
                  : "Show Advanced Settings"}
              </button>
            </div>

            {/* Collapsible Advanced Settings Section */}
            {showAdvancedSettings && (
              <div className="mt-3 p-3 border rounded bg-light">
                <h6 className="mb-3">Advanced Settings</h6>
                <label className="pe-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={deleteMode}
                    onChange={(e) => setDeleteMode(e.target.checked)}
                  />
                  <span className="ps-2 text-danger fw-bold">Delete</span>
                </label>
                <div className="mt-2">
                  <small className="text-muted">
                    Warning: Delete mode will remove records instead of adding
                    them.
                  </small>
                </div>
              </div>
            )}

            <div
              className={`ps-3 ${isMessageError ? "text-danger" : "text-success"}`}
            >
              {fileUploadMessage}
            </div>
            <SaveInProgress isVisible={saveInProgress} />

            <div></div>
          </div>
        </div>
      )}

      <DownloadDNCDNMDatabases access={access} />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="modal show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Confirm Delete Operation
                </h5>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <strong>Warning:</strong> This will delete the records instead
                  of add them.
                </div>
                <p>
                  Please type the word <strong>"delete"</strong> and click Ok to
                  continue.
                </p>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type 'delete' to confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDeleteCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleteConfirmText.toLowerCase() !== "delete"}
                >
                  Ok
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InternalDNCMain;
